import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';

const MONITOR_OPTIONS_DEFAULT = ['Single Monitor', 'Dual Monitor', 'Adjustable Monitor', 'Any Available'];
const NOTIFICATION_OPTIONS_DEFAULT = ['email', 'whatsapp'];

function LoginPage({ onLogin }) {
    const [step, setStep] = useState('email'); // email, password, profile
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [unit, setUnit] = useState('');
    const [userId, setUserId] = useState('');
    const [units, setUnits] = useState([]);
    const [alternateEmail, setAlternateEmail] = useState('');
    const [monitorPreference, setMonitorPreference] = useState(MONITOR_OPTIONS_DEFAULT[0]);
    const [notificationPreference, setNotificationPreference] = useState('whatsapp'); // Default to WhatsApp
    const [phoneCountryCode, setPhoneCountryCode] = useState('+62');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [monitorOptions, setMonitorOptions] = useState(MONITOR_OPTIONS_DEFAULT);
    const [notificationOptions, setNotificationOptions] = useState(NOTIFICATION_OPTIONS_DEFAULT);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [showWhatsAppPopup, setShowWhatsAppPopup] = useState(false);
    const [defaultAvatars, setDefaultAvatars] = useState([]);
    const [selectedAvatar, setSelectedAvatar] = useState('');
    const navigate = useNavigate();

    const resetProfileState = () => {
        setPassword('');
        setConfirmPassword('');
        setUnit('');
        setUnits([]);
        setAlternateEmail('');
        setMonitorPreference(MONITOR_OPTIONS_DEFAULT[0]);
        setMonitorOptions(MONITOR_OPTIONS_DEFAULT);
        setNotificationPreference(NOTIFICATION_OPTIONS_DEFAULT[0]);
        setNotificationOptions(NOTIFICATION_OPTIONS_DEFAULT);
        setPhoneCountryCode('+62');
        setPhoneNumber('');
        setShowWhatsAppPopup(false);
        setSelectedAvatar('');
    };

    // Fetch default avatars when profile step is reached
    useEffect(() => {
        if (step === 'profile') {
            axios.get('/api/profile/default-avatars')
                .then(response => setDefaultAvatars(response.data.avatars))
                .catch(err => console.error('Failed to load avatars:', err));
        }
    }, [step]);

    // Show WhatsApp popup when user selects WhatsApp preference
    const handleNotificationChange = (value) => {
        setNotificationPreference(value);
        if (value === 'whatsapp' && !showWhatsAppPopup) {
            setShowWhatsAppPopup(true);
        }
    };

    const applyLoginProfileDefaults = (data) => {
        const monitorList = data.monitor_options && data.monitor_options.length ? data.monitor_options : MONITOR_OPTIONS_DEFAULT;
        const notificationList = data.notification_options && data.notification_options.length ? data.notification_options : NOTIFICATION_OPTIONS_DEFAULT;
        setMonitorOptions(monitorList);
        setNotificationOptions(notificationList);
        setAlternateEmail(data.alternate_email || '');
        setMonitorPreference(data.monitor_preference || monitorList[0] || MONITOR_OPTIONS_DEFAULT[0]);
        setNotificationPreference(data.notification_preference || 'whatsapp'); // Default to WhatsApp
        setPhoneCountryCode(data.phone_country_code || '+62');
        setPhoneNumber(data.phone_number || '');
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await axios.post('/api/auth/login', { email });
            if (response.data.status === 'first_login') {
                setUserId(response.data.user_id);
                setUnits(response.data.units);
                applyLoginProfileDefaults(response.data);
                setStep('profile');
            } else if (response.data.status === 'prompt_password') {
                setUserId(response.data.user_id);
                applyLoginProfileDefaults(response.data);
                setStep('password');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await axios.post('/api/auth/verify-password', {
                user_id: userId,
                password,
            });
            onLogin(response.data.token, response.data.user);
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || 'Authentication failed');
        } finally {
            setLoading(false);
        }
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (!unit) {
            setError('Please select a unit');
            return;
        }

        // Validate WhatsApp number (now mandatory for all users)
        if (!phoneCountryCode || !phoneNumber) {
            setError('WhatsApp number is required for booking notifications');
            return;
        }

        if (!phoneCountryCode.startsWith('+')) {
            setError('Country code must start with + (e.g., +62)');
            return;
        }

        if (phoneNumber.length < 8 || phoneNumber.length > 15) {
            setError('Phone number must be between 8 and 15 digits');
            return;
        }

        // Remove non-digit characters from phone number
        const cleanPhoneNumber = phoneNumber.replace(/\D/g, '');

        setLoading(true);

        try {
            const response = await axios.post('/api/auth/complete-profile', {
                user_id: userId,
                password,
                unit,
                alternate_email: alternateEmail || null,
                monitor_preference: monitorPreference,
                notification_preference: notificationPreference,
                phone_country_code: phoneCountryCode,
                phone_number: cleanPhoneNumber,
                default_avatar: selectedAvatar || null,
            });
            onLogin(response.data.token, response.data.user);
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || 'Profile update failed');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await axios.post('/api/auth/forgot-password', { email: resetEmail });
            setShowForgotPassword(false);
            setError('');
            alert('Reset link sent to your email');
        } catch (err) {
            setError(err.response?.data?.error || 'Error sending reset link');
        } finally {
            setLoading(false);
        }
    };

    if (showForgotPassword) {
        return (
            <div className="login-container">
                <div className="login-box">
                    <img src="/assets/UNICEF_Logo.png" alt="UNICEF" className="login-logo" />
                    <h1>Desk Booking System</h1>
                    <h2>Reset Password</h2>
                    {error && <div className="error">{error}</div>}
                    <form onSubmit={handleForgotPassword}>
                        <div className="form-group">
                            <label>Email Address</label>
                            <input
                                type="email"
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                required
                                placeholder="hidsjakarta@unicef.org"
                            />
                        </div>
                        <button type="submit" disabled={loading} className="btn-submit">
                            {loading ? 'Sending...' : 'Send Reset Link'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowForgotPassword(false);
                                setResetEmail('');
                                setError('');
                            }}
                            className="btn-back"
                        >
                            Back to Login
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="login-box">
                <img src="/assets/UNICEF_Logo.png" alt="UNICEF" className="login-logo" />
                <h1>Desk Booking System</h1>

                {step === 'email' && (
                    <>
                        <h2>Welcome</h2>
                        <p className="subtitle">Enter your email to get started</p>
                        {error && <div className="error">{error}</div>}
                        <form onSubmit={handleEmailSubmit}>
                            <div className="form-group">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="hidsjakarta@unicef.org"
                                    autoFocus
                                />
                            </div>
                            <button type="submit" disabled={loading} className="btn-submit">
                                {loading ? 'Checking...' : 'Continue'}
                            </button>
                        </form>
                    </>
                )}

                {step === 'password' && (
                    <>
                        <h2>Enter Your Password</h2>
                        <p className="subtitle">Welcome back, {email}</p>
                        {error && <div className="error">{error}</div>}
                        <form onSubmit={handlePasswordSubmit}>
                            <div className="form-group">
                                <label>Password</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    placeholder="••••••••"
                                    autoFocus
                                />
                            </div>
                            <button type="submit" disabled={loading} className="btn-submit">
                                {loading ? 'Verifying...' : 'Sign In'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowForgotPassword(true)}
                                className="btn-forgot"
                            >
                                Forgot password?
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setStep('email');
                                    setPassword('');
                                    setError('');
                                }}
                                className="btn-back"
                            >
                                Use different email
                            </button>
                        </form>
                    </>
                )}

                {step === 'profile' && (
                    <>
                        <h2>Complete Your Profile</h2>
                        <p className="subtitle">First time login - set up your account</p>
                        {error && <div className="error">{error}</div>}
                        <form onSubmit={handleProfileSubmit}>
                            <div className="form-group">
                                <label>Password</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    placeholder="Create a password"
                                    autoFocus
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirm Password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    placeholder="Confirm password"
                                />
                            </div>
                            <div className="form-group">
                                <label>Select Your Unit/Section</label>
                                <select
                                    value={unit}
                                    onChange={(e) => setUnit(e.target.value)}
                                    required
                                >
                                    <option value="">-- Select Unit --</option>
                                    {units.map((u) => (
                                        <option key={u} value={u}>
                                            {u}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Alternate Email (optional)</label>
                                <input
                                    type="email"
                                    value={alternateEmail}
                                    onChange={(e) => setAlternateEmail(e.target.value)}
                                    placeholder="your.name@unicef.org"
                                />
                            </div>
                            <div className="form-group">
                                <label>Monitor Setup Preference</label>
                                <select
                                    value={monitorPreference}
                                    onChange={(e) => setMonitorPreference(e.target.value)}
                                >
                                    {monitorOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Notification Preference</label>
                                <select
                                    value={notificationPreference}
                                    onChange={(e) => handleNotificationChange(e.target.value)}
                                >
                                    {notificationOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option === 'email' ? 'Email Only' : 'WhatsApp (Recommended)'}
                                        </option>
                                    ))}
                                </select>
                                <p className="form-hint">We use this to confirm bookings and desk arrivals.</p>
                            </div>

                            <div className="form-group">
                                <label>WhatsApp Number (Required) *</label>
                                <p className="form-hint">Enter your WhatsApp number to receive booking notifications</p>
                                <div className="form-group-inline">
                                    <div className="form-group">
                                        <label>Country Code</label>
                                        <input
                                            type="text"
                                            value={phoneCountryCode}
                                            onChange={(e) => setPhoneCountryCode(e.target.value)}
                                            placeholder="+62"
                                            required
                                            maxLength="5"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Phone Number</label>
                                        <input
                                            type="tel"
                                            value={phoneNumber}
                                            onChange={(e) => setPhoneNumber(e.target.value)}
                                            placeholder="81212345678"
                                            required
                                            pattern="[0-9]{8,15}"
                                        />
                                    </div>
                                </div>
                                <p className="form-hint">Format: +[country code] [phone number without 0]. Example: +62 81234567890</p>
                            </div>
                            <div className="form-group">
                                <label>Choose Your Avatar (Optional)</label>
                                <p className="form-hint">Select a default avatar or upload your own later from your profile.</p>
                                <div className="avatar-gallery">
                                    {defaultAvatars.map((avatar) => (
                                        <div
                                            key={avatar}
                                            className={`avatar-option ${selectedAvatar === avatar ? 'selected' : ''}`}
                                            onClick={() => setSelectedAvatar(avatar)}
                                        >
                                            <img
                                                src={`/uploads/device-avatars/default avatar/${avatar}`}
                                                alt={avatar}
                                                className="avatar-preview"
                                            />
                                            {selectedAvatar === avatar && <div className="avatar-checkmark">✓</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <button type="submit" disabled={loading} className="btn-submit">
                                {loading ? 'Setting up...' : 'Complete Setup'}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setStep('email');
                                    resetProfileState();
                                    setError('');
                                }}
                                className="btn-back"
                            >
                                Back to Email
                            </button>
                        </form>
                    </>
                )}

                {/* WhatsApp Verification Popup */}
                {showWhatsAppPopup && (
                    <div className="whatsapp-popup-overlay" onClick={() => setShowWhatsAppPopup(false)}>
                        <div className="whatsapp-popup" onClick={(e) => e.stopPropagation()}>
                            <h3>Verify WhatsApp Connection</h3>
                            <p>To ensure you can receive booking confirmations via WhatsApp, please send a message to our system first.</p>
                            <div className="whatsapp-popup-actions">
                                <a
                                    href="https://wa.me/628115009000?text=Hello"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-whatsapp"
                                >
                                    Send WhatsApp Message
                                </a>
                                <button
                                    type="button"
                                    onClick={() => setShowWhatsAppPopup(false)}
                                    className="btn-later"
                                >
                                    I'll do this later
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="login-footer">
                    <p>© ICT Indonesia Desk Booking System 2025</p>
                </div>
            </div>
        </div>
    );
}

export default LoginPage;
